/**
 * The settings an Image or Video node runs with, as its chips show them and its send uses them:
 * each saved value is kept when the model offers it, else the model's default, else its first
 * option. The nodes and the Claude connector both read them here, so what Claude sets is what
 * the node shows and sends.
 */
import { pickSupportedOption, type ModelDefinition } from '../models/config';
import type { NodeData } from '../types';
import type { AudioRole } from './audioReference';

export interface ImageSettings {
    aspectRatio: string;
    /** OpenAI pixel sizes */
    size: string;
    /** Resolution tiers: 1K, 2K, 4K */
    resolution: string;
    /** GPT Image: low, medium, high */
    quality: string;
    numberOfImages: number;
    /** ComfyUI */
    steps: number;
}

/** How many pictures one send of an Image node can make with this model */
export const maxImagesOf = (model: ModelDefinition) => model.maxGeneratedImages || 4;

export const imageSettingsOf = (config: Partial<NonNullable<NodeData['imageGenConfig']>> | undefined, model: ModelDefinition): ImageSettings => ({
    aspectRatio: pickSupportedOption(config?.aspectRatio, model.supportedRatios, '1:1'),
    size: pickSupportedOption(config?.size, model.supportedSizes, '1024x1024'),
    resolution: pickSupportedOption(config?.resolution, model.supportedResolutions, model.defaultResolution || '1K'),
    quality: pickSupportedOption(config?.quality, model.supportedQualities, model.defaultQuality || 'high'),
    numberOfImages: Math.max(1, Math.min(config?.numberOfImages || 1, maxImagesOf(model))),
    steps: config?.steps || model.defaultSteps || 20,
});

export interface VideoSettings {
    aspectRatio: string;
    resolution: string;
    /** In seconds, as the model writes it ("8", or "5s" for Seedance) */
    duration: string;
    /** Seedance: make sound (on unless turned off) */
    audio: boolean;
    /** Connected audio as background music or as voice and sound references */
    audioRole: AudioRole;
}

export const videoSettingsOf = (config: Partial<NonNullable<NodeData['videoConfig']>> | undefined, model: ModelDefinition): VideoSettings => ({
    aspectRatio: pickSupportedOption(config?.aspectRatio, model.supportedRatios, '16:9'),
    resolution: pickSupportedOption(config?.resolution, model.supportedResolutions, '720p'),
    duration: pickSupportedOption(config?.duration, model.supportedDurations, '4'),
    audio: config?.audio ?? true,
    audioRole: config?.audioRole ?? 'music',
});
